import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
} from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import axios from "axios";

interface CreateQuestionnaireModalProps {
  open: boolean;
  onClose: () => void;
}

interface Question {
  id: string;
  text: string;
  type: "Yes/No" | "Multiple Choice" | "Likert Scale" | "Text";
  possibleAnswers: string[];
}

const CreateQuestionnaireModal: React.FC<CreateQuestionnaireModalProps> = ({
  open,
  onClose,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [newQuestion, setNewQuestion] = useState<Question>({
    id: "",
    text: "",
    type: "Yes/No",
    possibleAnswers: [],
  });
  const [newAnswer, setNewAnswer] = useState("");

  const handleAddQuestion = () => {
    if (newQuestion.text && newQuestion.type) {
      if (
        newQuestion.type === "Multiple Choice" &&
        newQuestion.possibleAnswers.length === 0
      ) {
        alert(
          "Please add at least one possible answer for Multiple Choice questions."
        );
        return;
      }
      setQuestions([
        ...questions,
        { ...newQuestion, id: Date.now().toString() },
      ]);
      setNewQuestion({
        id: "",
        text: "",
        type: "Yes/No",
        possibleAnswers: [],
      });
    }
  };

  const handleDeleteQuestion = (id: string) => {
    setQuestions(questions.filter((q) => q.id !== id));
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    const items = Array.from(questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setQuestions(items);
  };

  const handleAddAnswer = () => {
    if (newAnswer.trim() !== "") {
      setNewQuestion({
        ...newQuestion,
        possibleAnswers: [...newQuestion.possibleAnswers, newAnswer.trim()],
      });
      setNewAnswer("");
    }
  };

  const handleRemoveAnswer = (answer: string) => {
    setNewQuestion({
      ...newQuestion,
      possibleAnswers: newQuestion.possibleAnswers.filter((a) => a !== answer),
    });
  };

  const handleSave = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/questionnaires`,
        {
          title,
          description,
          questions: questions.map((q, index) => ({ ...q, order: index + 1 })),
        }
      );
      console.log("Questionnaire created:", response.data);
      onClose();
    } catch (error) {
      console.error("Error creating questionnaire:", error);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <Typography variant="h6" component="h2" gutterBottom>
          Create New Questionnaire
        </Typography>
        <TextField
          label="Questionnaire Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={4}
          margin="normal"
        />
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Add New Question
        </Typography>
        <TextField
          label="Question Text"
          value={newQuestion.text}
          onChange={(e) =>
            setNewQuestion({ ...newQuestion, text: e.target.value })
          }
          fullWidth
          margin="normal"
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Question Type</InputLabel>
          <Select
            value={newQuestion.type}
            onChange={(e) =>
              setNewQuestion({
                ...newQuestion,
                type: e.target.value as Question["type"],
                possibleAnswers: [],
              })
            }
          >
            <MenuItem value="Yes/No">Yes/No</MenuItem>
            <MenuItem value="Likert Scale">Likert Scale</MenuItem>
            <MenuItem value="Multiple Choice">Multiple Choice</MenuItem>
            <MenuItem value="Text">Text</MenuItem>
          </Select>
        </FormControl>
        {newQuestion.type === "Multiple Choice" && (
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Add Possible Answer"
              value={newAnswer}
              onChange={(e) => setNewAnswer(e.target.value)}
              fullWidth
              margin="normal"
            />
            <Button onClick={handleAddAnswer} variant="outlined" sx={{ mt: 1 }}>
              Add Answer
            </Button>
            <Box sx={{ mt: 2 }}>
              {newQuestion.possibleAnswers.map((answer, index) => (
                <Chip
                  key={index}
                  label={answer}
                  onDelete={() => handleRemoveAnswer(answer)}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          </Box>
        )}
        <Button onClick={handleAddQuestion} variant="contained" sx={{ mt: 2 }}>
          Add Question
        </Button>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Questions:
        </Typography>
        {questions.length === 0 ? (
          <Typography variant="body1" sx={{ mt: 2, fontStyle: "italic" }}>
            No questions added yet.
          </Typography>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="questions">
              {(provided) => (
                <List {...provided.droppableProps} ref={provided.innerRef}>
                  {questions.map((q, index) => (
                    <Draggable key={q.id} draggableId={q.id} index={index}>
                      {(provided) => (
                        <ListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => handleDeleteQuestion(q.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                        >
                          <DragIndicatorIcon sx={{ mr: 2 }} />
                          <ListItemText
                            primary={`Q${index + 1}: ${q.text}`}
                            secondary={
                              <>
                                Type: {q.type}
                                {q.type === "Multiple Choice" && (
                                  <Box sx={{ mt: 1 }}>
                                    Possible Answers:{" "}
                                    {q.possibleAnswers.join(", ")}
                                  </Box>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        )}
        <Button onClick={handleSave} variant="contained" sx={{ mt: 2 }}>
          Save Questionnaire
        </Button>
      </Box>
    </Modal>
  );
};

export default CreateQuestionnaireModal;
